/**
 * Écartés — dedicated list of candidates the user sidelined in one click
 * from their main queue. Different from /vivier (positive future re-engage)
 * and /refuses (definitive). These can be brought back into the active
 * queue in a single click.
 */
import { motion } from "framer-motion";
import { Archive, Loader2, RotateCcw, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { api } from "../lib/api";


export default function Ecartes() {
    const [loading, setLoading] = useState(true);
    const [candidates, setCandidates] = useState([]);
    const [search, setSearch] = useState("");
    const [working, setWorking] = useState(null);  // candidate id being restored

    async function reload() {
        const { data } = await api.get("/candidates", { params: { set_aside: "only", sort: "recent" } });
        setCandidates(data || []);
    }

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/candidates", { params: { set_aside: "only", sort: "recent" } });
                if (!cancelled) setCandidates(data || []);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!search) return candidates;
        const q = search.toLowerCase();
        return candidates.filter((c) =>
            `${c.first_name || ""} ${c.last_name || ""} ${c.email || ""} ${c.city || ""}`
                .toLowerCase()
                .includes(q),
        );
    }, [candidates, search]);

    async function restore(cid, name) {
        if (working) return;
        setWorking(cid);
        try {
            await api.post(`/candidates/${cid}/set-aside/restore`);
            toast.success(`${name} remis(e) dans la file`);
            await reload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de remettre dans la file");
        } finally {
            setWorking(null);
        }
    }

    if (loading) {
        return (
            <div className="flex justify-center py-20" data-testid="ecartes-loading">
                <Loader2 className="h-8 w-8 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-24 md:pb-8" data-testid="ecartes-page">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200 text-[10px] font-bold uppercase tracking-widest">
                        <Archive className="h-3 w-3" /> Hors file
                    </div>
                    <h1 className="mt-3 text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        Candidats écartés
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {candidates.length} candidat{candidates.length > 1 ? "s" : ""} mis{candidates.length > 1 ? "" : ""} de côté — exclus des Pépites, Alertes et files d'appel.
                    </p>
                </div>
            </div>

            {/* Search */}
            {candidates.length > 0 && (
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Rechercher un nom, email…"
                        data-testid="ecartes-search"
                        className="w-full pl-9 pr-3 h-10 rounded-full bg-white border border-shariti-gray text-sm focus:outline-none focus:border-shariti-orange transition"
                    />
                </div>
            )}

            {/* Empty state */}
            {filtered.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-shariti-gray" data-testid="ecartes-empty">
                    <Archive className="h-10 w-10 mx-auto text-slate-300" />
                    <p className="mt-4 font-bold text-shariti-black">
                        {candidates.length === 0 ? "Aucun candidat écarté" : "Aucun résultat"}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                        {candidates.length === 0
                            ? "Cliquez sur l'icône Archive sur une carte candidat pour le mettre de côté en 1 clic."
                            : "Aucun candidat ne correspond à votre recherche."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((c) => (
                        <motion.div
                            key={c.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-2xl border border-shariti-gray p-5 hover:shadow-md transition flex flex-col"
                            data-testid={`ecartes-card-${c.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="h-11 w-11 rounded-xl bg-slate-100 text-slate-500 font-extrabold text-sm flex items-center justify-center shrink-0">
                                    {c.first_name?.[0]}{c.last_name?.[0]}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <Link
                                        to={`/candidats/${c.id}`}
                                        className="text-base font-extrabold text-shariti-black hover:text-shariti-orange transition truncate block"
                                    >
                                        {c.first_name} {c.last_name}
                                    </Link>
                                    <div className="text-xs font-semibold text-slate-500 truncate">
                                        {c.layan_offer_title || c.source || "—"}
                                    </div>
                                </div>
                                {c.ai_score > 0 && (
                                    <div className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-shariti-cream text-slate-600 border border-shariti-gray shrink-0">
                                        IA {c.ai_score}
                                    </div>
                                )}
                            </div>
                            {c.set_aside_at && (
                                <div className="mt-3 text-[11px] font-semibold text-slate-500">
                                    Écarté le {new Date(c.set_aside_at).toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short", year: "numeric" })}
                                </div>
                            )}
                            <button
                                onClick={() => restore(c.id, c.first_name || "Le candidat")}
                                disabled={working === c.id}
                                data-testid={`ecartes-restore-${c.id}`}
                                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white px-4 h-9 font-bold text-xs transition shadow-btn disabled:opacity-50"
                            >
                                {working === c.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <RotateCcw className="h-3.5 w-3.5" />}
                                Remettre dans la file
                            </button>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
}
